import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { PRICE_SCHEDULE_MODES, NO_CHANGE_OF_INFO } from "../../common_nextgen/utils/Constants";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { ROUTE_PATHS } from "../../common_nextgen/utils/Constants";
import { formFields, screenInfo } from "./AdditionalPatientPayStepsFormConfig";
import { useModal } from "../../common_nextgen/hooks/ModalContext";
import { useLegacyLookup } from "./hooks/useLegacyLookup";

const AdditionalPatientPayStepScheduleForm = ({
  mode,
  initialSelectedPatient,
  params,
  schedule,
  selectMode,
  scheduleDesc,
  hideModal,
  selectParamsData,
  intialSchedule,
  setFalg,
}) => {
  const createObjectFromParams = (initialSchedule, params) => {
    const newObject = params.reduce((acc, param) => {
      const { source, target } = param;
      if (initialSchedule[source] !== undefined) {
        acc[target] = initialSchedule[source];
      }
      return acc;
    }, {});
    return newObject;
  };

  const result = selectMode
    ? createObjectFromParams(intialSchedule, params)
    : "";

  const { ADDITIONAL_PATIENT_PAY_STEPS } = ROUTE_PATHS;
  const { axios_helper_no_header } = useAxios();
  const location = useLocation();
  const { state: stateVal } = location;
  let [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  const {
    selectedSchedules = [],
    previousScreen = ADDITIONAL_PATIENT_PAY_STEPS,
    currentIndex: initialIndex = 0,
    scheduleName,
    scheduleDescription,
  } = currentState;
  const navigateToPage = useCustomNavigate();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const { showModal } = useModal();
  const { viewLookupDetails } = useLegacyLookup();
  const [lookupData, setLookupData] = useState({});

  const handleReceiveData = (data) => {
    if (data && Object.keys(data).length) {
      setLookupData(data);
    }
  };
  const fetchLookupData = (lookupScreen, params, formData) => {
    showModal(lookupScreen, { params }, handleReceiveData, formData);
  };

  const handleBack = () => {
    if (selectMode) {
      hideModal();
    } else if (currentIndex < selectedSchedules.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";
    let infoChanged = true;


    if (isAddMode(currentMode)) {
      url = "api/aptAddlPatPaySchedStps";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/aptAddlPatPaySchedStps";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        infoChanged = false;
      }
    }

    const data = { ...dataForSubmission, screenNumber, infoChanged };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
       if (responseStatus.message === NO_CHANGE_OF_INFO) {
                                    toast.current.show({
                                      severity: "info",
                                      summary: "",
                                      detail: responseStatus.message,
                                      life: 3000,
                                    });
                                  } else {
                                    toast.current.show({
                                      severity: "success",
                                      summary: "",
                                      detail: responseStatus.message,
                                      life: 3000,
                                    }); 
      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        currentState = {
          ...currentState,
          selectedSchedules: [
            { ...responseData, apsAddtlPatPaySchDesc: scheduleDescription },
          ],
          currentIndex: 0,
        };
        navigateToPage(ROUTE_PATHS.ADDITIONAL_PATIENT_PAY_STEPS_EDIT, [
          ...previousState,
          currentState,
        ]);
      } else {
        setInitialFormData({
          ...responseData,
          apsAddtlPatPaySchDesc: scheduleDescription,
        });
      }
    } }catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.F14 = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.F14 = "View";
    } else if (isReadOnlyMode(currentMode)) {
      if (selectMode) {
        keyFunc.F12 = handleBack;
        keyName.F12 = "Back";
      } else {
        keyFunc.F12 = handleBack;
        keyName.F12 = "Back";
        keyFunc.F14 = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        keyName.F14 = "Edit";
      }
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, initialFormData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const params = isAddMode(currentMode)
        ? {
            apsAddtlPatPaySch: selectMode
              ? result?.apsAddtlPatPaySch
              : scheduleName,
          }
        : {
            aptStepsNbr: selectMode
              ? result?.aptStepsNbr ?? null
              : selectedSchedules[currentIndex].aptStepsNbr,
          };

      const baseUrl = isAddMode(currentMode)
        ? `api/aptAddlPatPaySchedStps/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`
        : selectMode
        ? `api/aptAddlPatPaySchedStps/aptSteps/${encodeURIComponent(
            selectedSchedules[currentIndex].apsAddtlPatPaySch
          )}`
        : `api/aptAddlPatPaySchedStps/aptSteps/${encodeURIComponent(
            selectedSchedules[currentIndex].apsAddtlPatPaySch
          )}`;

      if (selectMode) {
        const queryParams = new URLSearchParams();
        queryParams.append("aptStepsNbr", result?.aptStepsNbr);

        const finalUrl = `${baseUrl}${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;

        const { responseData } = await axios_helper_no_header("GET", finalUrl);

        setInitialFormData({
          ...responseData,
          apsAddtlPatPaySch:
            typeof responseData.apsAddtlPatPaySch === "object"
              ? JSON.stringify(responseData.apsAddtlPatPaySch)
              : responseData.apsAddtlPatPaySch,
        });
      } else {
        const { responseData } = await axios_helper_no_header(
          "GET",
          baseUrl,
          {},
          params
        );
        console.log("adresponse", responseData);
        setInitialFormData({
          ...responseData,
          apsAddtlPatPaySch: scheduleName,
          apsAddtlPatPaySchDesc: scheduleDescription,
        });
      }
    };

    fetchInitialData();
  }, [currentMode, currentIndex]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
          fetchLookupData={fetchLookupData}
          lookupData={lookupData}
          viewLookupDetails={viewLookupDetails}
        />
      </div>
    </div>
  );
};

export default AdditionalPatientPayStepScheduleForm;
